import { useMutation, useQuery } from '@tanstack/react-query';

import {
	createAdmin,
	deleteAdmin,
	getAdmin,
	getAdmins,
	updateAdmin,
} from '../../api/admin/adminApi';
import { queryClient } from '../../../lib/query';

export const useGetAdmins = (params?: Partial<Parameters<typeof getAdmins>[0]>) => {
	const resolvedParams = {
		page: params?.page ?? 1,
		limit: params?.limit ?? 10,
	};

	return useQuery({
		queryKey: ['admins', resolvedParams],
		queryFn: async () => {
			const response = await getAdmins(resolvedParams);
			return response;
		},
	});
};

export const useGetAdmin = (id?: number) => {
	return useQuery({
		queryKey: ['admin', id],
		queryFn: async () => {
			if (typeof id !== 'number') throw new Error('Admin id is required');
			const response = await getAdmin(id);
			return response;
		},
		enabled: typeof id === 'number',
	});
};

export const useCreateAdmin = () => {
	return useMutation({
		mutationKey: ['create-admin'],
		mutationFn: async (payload: Parameters<typeof createAdmin>[0]) => {
			const response = await createAdmin(payload);
			return response;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['admins'], exact: false });
		},
	});
};

export const useUpdateAdmin = () => {
	return useMutation({
		mutationKey: ['update-admin'],
		mutationFn: async (args: { id: number; payload: Parameters<typeof updateAdmin>[1] }) => {
			const response = await updateAdmin(args.id, args.payload);
			return response;
		},
		onSuccess: async (_data, variables) => {
			await queryClient.invalidateQueries({ queryKey: ['admins'], exact: false });
			await queryClient.invalidateQueries({ queryKey: ['admin', variables.id] });
		},
	});
};

export const useDeleteAdmin = () => {
	return useMutation({
		mutationKey: ['delete-admin'],
		mutationFn: async (id: number) => {
			await deleteAdmin(id);
		},
		onSuccess: async (_data, id) => {
			await queryClient.invalidateQueries({ queryKey: ['admins'], exact: false });
			await queryClient.invalidateQueries({ queryKey: ['admin', id] });
		},
	});
};

